import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiShield, FiUploadCloud } from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { client } from "../../Clients";
import { showSuccessModal } from "../../Helper/showSuccessModal";
import { normalizeDeployResult } from "./deploymentUtils";
import DeploymentPanel from "./DeploymentPanel";
import ActivityFeed from "./ActivityFeed";
import ConnectionsModalButton from "../../Components/ConnectionsModalButton";
import { useDeploymentSummary, type DeploymentSummary } from "./useDeploymentSummary";
import { usePromoteToProduction } from "./DeploymentOverview";

type Target = "preview" | "production";

const pendingTotal = (s: DeploymentSummary): number =>
	s.totals.outOfSyncPages + s.totals.outOfSyncSingletons + s.totals.queued;

/** One environment tab with its live pending-count badge. */
const Tab: React.FC<{ active: boolean; label: string; count: number; tone: "preview" | "production"; onClick: () => void }> = ({
	active,
	label,
	count,
	tone,
	onClick,
}) => {
	const activeRing = tone === "production" ? "border-orange-300 text-orange-700" : "border-emerald-300 text-emerald-700";
	return (
		<button
			type="button"
			onClick={onClick}
			className={`flex items-center gap-2 rounded-t-xl border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
				active ? `${activeRing} bg-white` : "border-transparent text-slate-500 hover:text-slate-700"
			}`}
		>
			{tone === "production" && <FiShield className={`h-3.5 w-3.5 ${active ? "text-orange-500" : "text-slate-400"}`} />}
			{label}
			<span
				className={`rounded-full px-2 py-0.5 text-xs font-bold ${
					count === 0 ? "bg-slate-100 text-slate-400" : tone === "production" ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"
				}`}
			>
				{count}
			</span>
		</button>
	);
};

const HeroStat: React.FC<{ label: string; value: number; accent?: boolean }> = ({ label, value, accent }) => (
	<div className={`rounded-xl border px-3 py-2 text-center ${accent ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-slate-50"}`}>
		<div className={`text-lg font-bold ${accent ? "text-orange-700" : "text-slate-800"}`}>{value}</div>
		<div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
	</div>
);

/** Preview and production deployment, one target at a time, with a live automatic-activity feed. */
const Deployment: React.FC = () => {
	const [target, setTarget] = useState<Target>("preview");
	const [deployingPreview, setDeployingPreview] = useState(false);
	const [analyzingPreview, setAnalyzingPreview] = useState(false);
	const [analyzingProduction, setAnalyzingProduction] = useState(false);
	const [message, setMessage] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [previewDeployVersion, setPreviewDeployVersion] = useState(0);
	const [productionDeployVersion, setProductionDeployVersion] = useState(0);
	const [showProductionConfirm, setShowProductionConfirm] = useState(false);

	usePageHeader({ title: "Deployment" });

	// Per-target summaries drive the tab badges + the status hero.
	const preview = useDeploymentSummary("preview");
	const production = useDeploymentSummary("production");
	useEffect(() => { preview.load(previewDeployVersion); }, [preview.load, previewDeployVersion]);
	useEffect(() => { production.load(productionDeployVersion); }, [production.load, productionDeployVersion]);
	// A preview deploy changes what's promotable → refresh production too.
	useEffect(() => { if (previewDeployVersion > 0) production.load(previewDeployVersion + 1); }, [production.load, previewDeployVersion]);

	const handleDeployPreview = useCallback(async () => {
		setDeployingPreview(true);
		setError(null);
		setMessage(null);
		try {
			const deployResponse = await client.deployment.apiDeploymentDeploymentQueueDeployPost("preview", {
				withCredentials: true,
				headers: { Accept: "application/json" },
			});
			const deployResult = normalizeDeployResult(deployResponse.data);
			if (deployResult.failed > 0) {
				setError(`Preview deployed with ${deployResult.failed} error(s).`);
			} else {
				const messageText =
					deployResult.total > 0 ? `Preview deployed (${deployResult.succeeded}/${deployResult.total}).` : "Preview deployed.";
				setMessage(messageText);
				showSuccessModal({ title: "Preview deploy complete", message: messageText });
			}
			setPreviewDeployVersion((v) => v + 1);
		} catch (err: any) {
			console.error(err);
			setError(err?.response?.status === 409 ? "Another deployment is already in progress. Please wait and try again." : "Failed to deploy preview.");
		} finally {
			setDeployingPreview(false);
		}
	}, []);

	const { promote, running: promoting, result: promoteResult } = usePromoteToProduction(() => {
		setProductionDeployVersion((v) => v + 1);
	});

	useEffect(() => { if (promoteResult) setMessage(promoteResult); }, [promoteResult]);

	const active = target === "preview" ? preview.summary : production.summary;
	const isProd = target === "production";
	const activePending = pendingTotal(active);
	const busy = deployingPreview || promoting;

	const statusLine = isProd
		? activePending > 0
			? `${activePending} changement${activePending > 1 ? "s" : ""} à promouvoir`
			: "Production à jour"
		: activePending > 0
		? `${activePending} changement${activePending > 1 ? "s" : ""} en attente`
		: "Préversion à jour";

	return (
		<div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
			{/* Production promote confirmation dialog */}
			{showProductionConfirm && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
					<div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
						<div className="mb-4 flex items-center gap-3">
							<div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
								<FiShield className="h-5 w-5 text-orange-600" />
							</div>
							<div>
								<h3 className="text-lg font-bold text-slate-900">Promote to Production</h3>
								<p className="text-sm text-slate-500">This pushes everything staged in preview to the live site.</p>
							</div>
						</div>
						<p className="mb-6 text-sm text-slate-700">
							All out-of-sync page shells and queued records will be deployed to{" "}
							<span className="font-semibold text-orange-700">production</span>. This is visible to end users immediately.
						</p>
						<div className="flex items-center justify-end gap-3">
							<button
								type="button"
								onClick={() => setShowProductionConfirm(false)}
								className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => { setShowProductionConfirm(false); promote(); }}
								className="rounded-lg border border-orange-300 bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600"
							>
								Confirm promotion
							</button>
						</div>
					</div>
				</div>
			)}

			{error && (
				<div className="mx-4 mt-2 flex shrink-0 items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
					<FiAlertTriangle /> {error}
				</div>
			)}
			{message && (
				<div className="mx-4 mt-2 flex shrink-0 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
					<FiCheckCircle /> {message}
				</div>
			)}

			{/* Environment tabs */}
			<div className="shrink-0 flex items-end gap-1 border-b border-slate-200 px-4 pt-3">
				<Tab active={target === "preview"} label="Preview" count={pendingTotal(preview.summary)} tone="preview" onClick={() => setTarget("preview")} />
				<Tab active={target === "production"} label="Production" count={pendingTotal(production.summary)} tone="production" onClick={() => setTarget("production")} />
				<div className="ml-auto pb-1">
					<ConnectionsModalButton className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" />
				</div>
			</div>

			{/* Active target: hero on top, then changes (3/4) + activity (1/4) side by side */}
			<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4">
					{/* Status hero */}
					<section className={`shrink-0 rounded-2xl border bg-white p-5 shadow-sm ${isProd ? "border-orange-200" : "border-slate-200"}`}>
						<div className="flex flex-wrap items-center justify-between gap-3">
							<div className="flex items-center gap-3">
								<span className={`flex h-2.5 w-2.5 rounded-full ${activePending > 0 ? (isProd ? "bg-orange-500" : "bg-emerald-500") : "bg-slate-300"}`} />
								<div>
									<h2 className="text-lg font-bold text-slate-900">{isProd ? "Production" : "Preview"}</h2>
									<p className="text-sm text-slate-500">{statusLine}</p>
								</div>
							</div>
							{isProd ? (
								<button
									type="button"
									onClick={() => setShowProductionConfirm(true)}
									disabled={busy || activePending === 0}
									className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300"
								>
									<FiUploadCloud className={promoting ? "animate-pulse" : ""} />
									{promoting ? "Promotion…" : "Promote to Production"}
								</button>
							) : (
								<button
									type="button"
									onClick={handleDeployPreview}
									disabled={busy || active.totals.queued === 0}
									className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
								>
									<FiUploadCloud className={deployingPreview ? "animate-pulse" : ""} />
									{deployingPreview ? "Deploying…" : "Deploy to Preview"}
								</button>
							)}
						</div>
						<div className="mt-4 grid grid-cols-3 gap-2">
							<HeroStat label="Pages" value={active.totals.outOfSyncPages} accent={isProd} />
							<HeroStat label="Singletons" value={active.totals.outOfSyncSingletons} />
							<HeroStat label="Queued" value={active.totals.queued} />
						</div>
					</section>

				{/* Changes & analysis (2/3) + activity (1/3) — both fill to the bottom, scroll internally */}
				<div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden lg:grid-cols-3">
					<div className="min-h-0 lg:col-span-2">
						{target === "preview" ? (
							<DeploymentPanel
								key="preview"
								title="Preview"
								target="preview"
								deployLabel="Deploy to Preview"
								onDeploy={handleDeployPreview}
								deploying={deployingPreview}
								hideDeploy
								hideTitle
								refreshAfterDeployTrigger={previewDeployVersion}
								onAnalyzingChange={setAnalyzingPreview}
								disableAnalyze={analyzingProduction}
							/>
						) : (
							<DeploymentPanel
								key="production"
								title="Production"
								target="production"
								deployLabel="Promote to Production"
								onDeploy={() => setShowProductionConfirm(true)}
								deploying={promoting}
								hideDeploy
								hideTitle
								refreshAfterDeployTrigger={productionDeployVersion}
								refreshAfterPreviewDeployTrigger={previewDeployVersion}
								onAnalyzingChange={setAnalyzingProduction}
								disableAnalyze={analyzingPreview}
							/>
						)}
					</div>
					<div className="min-h-0 lg:col-span-1">
						<ActivityFeed target={target} refreshKey={isProd ? productionDeployVersion : previewDeployVersion} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default Deployment;
